import type { ApiResponse } from '@shared'
import { useToastStore } from '../stores/toastStore'
import { WORKER_URL, publicFileUrl, downloadUrl } from '../config'

interface RateLimitDetails {
  limit?: string;
  usage?: number;
  resetIn?: number;
  blockedOperations?: string[];
}

interface CSRFErrorDetails {
  code?: string;
  hint?: string;
  canRefresh?: boolean;
}

// Shape of a JSON error/response body the client may read off a failed request.
interface ErrorResponseData {
  error?: string;
  message?: string;
  details?: RateLimitDetails;
  code?: string;
  hint?: string;
  canRefresh?: boolean;
  data?: { csrfToken?: string };
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public details?: RateLimitDetails,
    public csrfDetails?: CSRFErrorDetails
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private handleRateLimitError(error: ApiError): void {
    const addToast = useToastStore.getState().addToast
    
    if (error.details?.limit) {
      let message = ''
      
      switch (error.details.limit) {
        case 'storage':
          message = `💾 Storage Limit Reached - Storage is at ${error.details.usage?.toFixed(1)}% capacity. Delete some files to free up space.`
          break
        case 'classA':
          message = `✏️ Write Operations Blocked - Monthly write operations limit reached (${error.details.usage?.toFixed(1)}%). Operations will reset in ${error.details.resetIn} days.`
          break
        case 'classB':
          message = `👁️ Read Operations Blocked - Monthly read operations limit reached (${error.details.usage?.toFixed(1)}%). Operations will reset in ${error.details.resetIn} days.`
          break
      }
      
      // Show what operations are blocked
      if (error.details.blockedOperations?.length) {
        message += ' Blocked: ' + error.details.blockedOperations.join(', ')
      }
      
      addToast({
        type: 'error',
        message,
        duration: 10000, // Longer duration for rate limit errors
      })
    } else {
      addToast({
        type: 'error',
        message: `🚫 Operation Blocked - ${error.message || 'This operation has been blocked due to usage limits.'}`,
        duration: 8000,
      })
    }
  }

  private async request<T = unknown>(
    endpoint: string,
    options: RequestInit = {},
    token?: string,
    csrfToken?: string
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> | undefined),
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    if (csrfToken && options.method && !['GET', 'HEAD', 'OPTIONS'].includes(options.method.toUpperCase())) {
      headers['X-CSRF-Token'] = csrfToken
      console.log('🔒 CSRF token added for', options.method, endpoint)
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      })

      // Handle rate limit errors (429)
      if (response.status === 429) {
        let errorData: ErrorResponseData = {}
        try {
          errorData = await response.json()
        } catch {
          errorData = { error: 'Rate limit exceeded' }
        }
        
        const error = new ApiError(
          errorData.error || 'Rate limit exceeded',
          429,
          errorData.details
        )
        
        this.handleRateLimitError(error)
        throw error
      }

      // Handle authentication errors (401 Unauthorized)
      // Exceptions that must NOT trigger the "session expired" redirect:
      //   • /api/login — wrong password (never authenticated)
      //   • admin-only widgets a team caller might hit inadvertently
      //     (StorageCounter, kill-switch, admin/*, security/*, cost/*).
      //     These return 401 because the role is insufficient, not because the
      //     session expired — nuking the session would be a false positive.
      const nonSessionEndpoints = [
        '/api/login',
        '/api/storage/usage',
        '/api/kill-switch/',
        '/api/admin/',
        '/api/security/',
        '/api/cost/',
      ];
      const skipAutoLogout = nonSessionEndpoints.some(p => endpoint.startsWith(p));
      if (response.status === 401 && !skipAutoLogout) {
        const addToast = useToastStore.getState().addToast

        console.warn('⚠️ Session expired - redirecting to login')

        try {
          localStorage.removeItem('vendetta-token')
          localStorage.removeItem('vendetta-csrf')
          localStorage.removeItem('vendetta-role')
        } catch {}

        addToast({
          type: 'warning',
          message: 'Session expired. Please login again.'
        })

        setTimeout(() => {
          window.location.href = '/'
        }, 1000)

        throw new ApiError('Session expired', 401)
      }

      // Handle forbidden errors (403)
      if (response.status === 403) {
        const addToast = useToastStore.getState().addToast
        
        let errorData: ErrorResponseData = {}
        try {
          errorData = await response.json()
        } catch {}
        
        const errorMessage = errorData.error || 'Access denied'
        
        // Check if it's a CSRF error that can be refreshed
        if (errorData.code === 'CSRF_INVALID' && errorData.canRefresh) {
          console.warn('🔄 CSRF token invalid, attempting refresh...')
          
          // Try to refresh CSRF token
          const { useAuthStore } = await import('../stores/authStore')
          const newCsrfToken = await useAuthStore.getState().refreshCSRF()
          
          if (newCsrfToken) {
            console.log('✅ CSRF refreshed, retrying request...')
            
            // Retry the original request with new CSRF token
            const retryHeaders: Record<string, string> = { ...headers }
            retryHeaders['X-CSRF-Token'] = newCsrfToken
            
            const retryResponse = await fetch(url, {
              ...options,
              headers: retryHeaders,
            })
            
            if (retryResponse.ok) {
              const retryData = await retryResponse.json()
              
              // Update CSRF if response contains new token
              if (retryData.data?.csrfToken) {
                useAuthStore.getState().updateCSRF(retryData.data.csrfToken)
              }
              
              return retryData
            }
          }
          
          // Refresh failed, show error
          addToast({
            type: 'warning',
            message: 'Session security token expired. Please refresh the page or re-login.'
          })
          
          throw new ApiError(errorMessage, 403, undefined, {
            code: errorData.code,
            hint: errorData.hint,
            canRefresh: errorData.canRefresh
          })
        }
        
        console.warn('🚫 Access denied:', errorMessage)
        
        addToast({
          type: 'error',
          message: `Access denied: ${errorMessage}`
        })
        
        throw new ApiError(errorMessage, 403)
      }

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`
        try {
          const errorData = await response.json()
          if (errorData.error) {
            errorMessage = errorData.error
          }
        } catch {
          // Use default error message if JSON parsing fails
        }
        
        throw new ApiError(errorMessage, response.status)
      }

      const data = await response.json()
      
      // Update CSRF token if response contains a new one
      if (data.data?.csrfToken) {
        try {
          const { useAuthStore } = await import('../stores/authStore')
          useAuthStore.getState().updateCSRF(data.data.csrfToken)
        } catch (e) {
          // Silently fail if store not available
          console.debug('Could not update CSRF from response:', e)
        }
      }
      
      return data
    } catch (error) {
      if (error instanceof ApiError) {
        throw error
      }
      
      console.error(`❌ API request failed: ${endpoint}`, error)
      throw error
    }
  }

  async get<T = unknown>(endpoint: string, token?: string, csrfToken?: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' }, token, csrfToken)
  }

  async post<T = unknown>(endpoint: string, body: unknown, token?: string, csrfToken?: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    }, token, csrfToken)
  }

  async put<T = unknown>(endpoint: string, body: unknown, token?: string, csrfToken?: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    }, token, csrfToken)
  }

  async delete<T = unknown>(endpoint: string, body?: unknown, token?: string, csrfToken?: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    }, token, csrfToken)
  }

  async uploadFile(
    file: File,
    parentPath: string,
    isPublic: boolean,
    token: string,
    fileName?: string,
    csrfToken?: string
  ): Promise<ApiResponse> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('path', parentPath)
    formData.append('isPublic', isPublic.toString())
    
    if (fileName) {
      formData.append('fileName', fileName)
    }

    const url = `${this.baseUrl}/api/upload`
    
    const headers: Record<string, string> = {
      'Authorization': `Bearer ${token}`,
    }
    
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken
      console.log('🔒 CSRF token added for file upload')
    }
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      })

      // Handle rate limit for file uploads
      if (response.status === 429) {
        let errorData: ErrorResponseData = {}
        try {
          errorData = await response.json()
        } catch {
          errorData = { error: 'Upload blocked due to usage limits' }
        }
        
        const error = new ApiError(
          errorData.error || 'Upload blocked',
          429,
          errorData.details
        )
        
        this.handleRateLimitError(error)
        throw error
      }

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`
        try {
          const errorData = await response.json()
          if (errorData.error) {
            errorMessage = errorData.error
          }
        } catch {
          // Use default error message
        }
        
        throw new Error(errorMessage)
      }

      return await response.json()
    } catch (error) {
      if (error instanceof ApiError) {
        throw error
      }
      
      console.error('❌ File upload failed:', error)
      throw error
    }
  }

  // ✅ NEW: Unzip file method
  async unzipFile(
    filePath: string,
    targetPath: string,
    token: string,
    csrfToken?: string
  ): Promise<ApiResponse> {
    console.log('📦 [API] Unzip request:', { filePath, targetPath })
    
    return this.post('/api/files/unzip', {
      filePath,
      targetPath
    }, token, csrfToken)
  }

  async getStorageUsage(token: string, csrfToken?: string): Promise<ApiResponse> {
    return this.get('/api/storage/usage', token, csrfToken)
  }

  async getR2Stats(token: string, csrfToken?: string): Promise<ApiResponse> {
    return this.get('/api/r2/stats', token, csrfToken)
  }

  async search(query: string, path: string, token: string, csrfToken?: string): Promise<ApiResponse> {
    const params = new URLSearchParams({ q: query, path })
    return this.get(`/api/search?${params.toString()}`, token, csrfToken)
  }

  async advancedSearch(params: {
    query: string;
    path?: string;
    fileType?: string;
    minSize?: number;
    maxSize?: number;
    dateFrom?: string;
    dateTo?: string;
    searchContent?: boolean;
    isPublic?: string;
  }, token: string, csrfToken?: string): Promise<ApiResponse> {
    const searchParams = new URLSearchParams({
      q: params.query,
      ...(params.path && { path: params.path }),
      ...(params.fileType && { type: params.fileType }),
      ...(params.minSize && { minSize: params.minSize.toString() }),
      ...(params.maxSize && { maxSize: params.maxSize.toString() }),
      ...(params.dateFrom && { dateFrom: params.dateFrom }),
      ...(params.dateTo && { dateTo: params.dateTo }),
      ...(params.searchContent !== undefined && { content: params.searchContent.toString() }),
      ...(params.isPublic && { public: params.isPublic })
    })
    
    return this.get(`/api/search/advanced?${searchParams.toString()}`, token, csrfToken)
  }

  // ============ KILL SWITCH METHODS ============
  
  async getKillSwitchStatus(token?: string, csrfToken?: string): Promise<ApiResponse> {
    return this.get('/api/kill-switch/status', token, csrfToken)
  }

  async toggleKillSwitch(type: string, active: boolean, reason: string | null, token: string, csrfToken?: string): Promise<ApiResponse> {
    return this.post('/api/kill-switch/toggle', { type, active, reason }, token, csrfToken)
  }

  async blockResource(resource_type: string, resource_id: string, active: boolean, reason: string | null, token: string, csrfToken?: string): Promise<ApiResponse> {
    return this.post('/api/kill-switch/resource', { resource_type, resource_id, active, reason }, token, csrfToken)
  }

  async getKillSwitchLogs(token: string, csrfToken?: string): Promise<ApiResponse> {
    return this.get('/api/kill-switch/logs', token, csrfToken)
  }

  async createKillSwitchLog(action: string, details: string | null, token: string, csrfToken?: string): Promise<ApiResponse> {
    return this.post('/api/kill-switch/log', { action, details }, token, csrfToken)
  }

  async removeKillSwitchResource(id: string, token: string, csrfToken?: string): Promise<ApiResponse> {
    return this.delete(`/api/kill-switch/resource/${id}`, undefined, token, csrfToken)
  }

  async checkKillSwitch(type: string, id: string, token: string, csrfToken?: string): Promise<ApiResponse> {
    return this.get(`/api/kill-switch/check/${type}/${id}`, token, csrfToken)
  }

  async getKillSwitchStats(token: string, csrfToken?: string): Promise<ApiResponse> {
    return this.get('/api/kill-switch/stats', token, csrfToken)
  }

  // ============ TEAM AMP SUBMISSION PORTAL ============

  async teamLogin(password: string, turnstileToken: string): Promise<ApiResponse<{ token: string; expiresIn: number }>> {
    return this.post('/api/team/login', { password, turnstileToken })
  }

  async teamLogout(token: string): Promise<ApiResponse> {
    return this.post('/api/team/logout', {}, token)
  }

  async teamUploadAmp(file: File, slug: string, token: string): Promise<ApiResponse<{
    id: string
    slug: string
    path: string
    url: string
    activeHost: string | null
    rateLimit: { used: number; limit: number }
  }>> {
    const form = new FormData()
    form.append('file', file)
    if (slug) form.append('slug', slug)

    const res = await fetch(`${this.baseUrl}/api/team/amp/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    })

    let data: any = null
    try { data = await res.json() } catch { /* ignore */ }
    if (!res.ok || !data?.success) {
      throw new ApiError(data?.error || `HTTP ${res.status}`, res.status)
    }
    return data
  }

  async teamAmpList(token: string): Promise<ApiResponse<{
    items: Array<{ id: string; name: string; path: string; size: number; createdAt: string; url: string }>
    activeHost: string | null
  }>> {
    return this.get('/api/team/amp/list', token)
  }

  async teamAmpGetContent(path: string, token: string): Promise<ApiResponse<{
    id: string; name: string; path: string; size: number; content: string
  }>> {
    return this.get(`/api/team/amp/content?path=${encodeURIComponent(path)}`, token)
  }

  async teamAmpUpdate(path: string, content: string, token: string): Promise<ApiResponse<{
    id: string; path: string; size: number; rateLimit: { used: number; limit: number }
  }>> {
    return this.put('/api/team/amp/content', { path, content }, token)
  }

  async teamAmpDelete(path: string, token: string): Promise<ApiResponse> {
    return this.delete(`/api/team/amp?path=${encodeURIComponent(path)}`, undefined, token)
  }

  async getRecentFiles(limit: number, token: string, csrfToken?: string): Promise<ApiResponse<{
    items: Array<{
      id: string; name: string; path: string; size: number
      mimeType: string; uploadDate: string; isPublic: boolean
    }>
  }>> {
    return this.get(`/api/files/recent?limit=${limit}`, token, csrfToken)
  }

  // ============ TRASH / RECYCLE BIN ============

  async listTrash(token: string, csrfToken?: string): Promise<ApiResponse<{
    files: Array<{ id: string; name: string; path: string; size: number; mimeType: string; deletedAt: string; type: 'file' }>
    folders: Array<{ id: string; name: string; path: string; deletedAt: string; type: 'folder' }>
  }>> {
    return this.get('/api/trash', token, csrfToken)
  }

  async restoreTrashItem(itemId: string, itemType: 'file' | 'folder', token: string, csrfToken?: string): Promise<ApiResponse> {
    return this.post('/api/trash/restore', { itemId, itemType }, token, csrfToken)
  }

  async purgeTrashItem(itemId: string, itemType: 'file' | 'folder', token: string, csrfToken?: string): Promise<ApiResponse> {
    return this.delete(`/api/trash?itemId=${encodeURIComponent(itemId)}&itemType=${itemType}`, undefined, token, csrfToken)
  }

  async emptyTrash(token: string, csrfToken?: string): Promise<ApiResponse> {
    return this.delete('/api/trash/empty', undefined, token, csrfToken)
  }

  // ============ AMP DOMAIN ROTATION ============

  async getAmpDomain(token: string, csrfToken?: string): Promise<ApiResponse<{
    active: string | null
    available: string[]
    envBaseline: string[]
  }>> {
    return this.get('/api/admin/amp-domain', token, csrfToken)
  }

  async setAmpDomain(domain: string, token: string, csrfToken?: string): Promise<ApiResponse> {
    return this.put('/api/admin/amp-domain', { domain }, token, csrfToken)
  }

  async addAmpDomain(subdomain: string, token: string, csrfToken?: string): Promise<ApiResponse> {
    return this.post('/api/admin/amp-domain/add', { subdomain }, token, csrfToken)
  }

  async removeAmpDomain(hostname: string, token: string, csrfToken?: string): Promise<ApiResponse> {
    return this.delete(`/api/admin/amp-domain/${encodeURIComponent(hostname)}`, undefined, token, csrfToken)
  }

  getPublicFileUrl(filePath: string): string {
    return publicFileUrl(filePath)
  }

  getDownloadUrl(filePath: string): string {
    return downloadUrl(filePath)
  }
}

export const api = new ApiClient(WORKER_URL)

export async function makeApiRequest(endpoint: string, options?: RequestInit): Promise<any> {
  const token = localStorage.getItem('vendetta-token')
  const csrfToken = localStorage.getItem('vendetta-csrf')
  
  const method = options?.method || 'GET'
  
  let body = undefined
  if (options?.body && typeof options.body === 'string') {
    try {
      body = JSON.parse(options.body)
    } catch {
      body = options.body
    }
  }
  
  switch (method.toUpperCase()) {
    case 'GET':
      const response = await api.get(endpoint, token || undefined, csrfToken || undefined)
      return response.data
      
    case 'POST':
      const postResponse = await api.post(endpoint, body, token || undefined, csrfToken || undefined)
      return postResponse.data
      
    case 'PUT':
      const putResponse = await api.put(endpoint, body, token || undefined, csrfToken || undefined)
      return putResponse.data
      
    case 'DELETE':
      const deleteResponse = await api.delete(endpoint, body, token || undefined, csrfToken || undefined)
      return deleteResponse.data
      
    default:
      throw new Error(`Unsupported method: ${method}`)
  }
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export function getFileType(mimeType: string): string {
  if (mimeType.startsWith('image/')) return 'image'
  if (mimeType.startsWith('video/')) return 'video'
  if (mimeType.startsWith('audio/')) return 'audio'
  if (mimeType.startsWith('text/')) return 'text'
  if (mimeType === 'application/pdf') return 'document'
  if (mimeType.includes('document') || mimeType.includes('sheet') || mimeType.includes('presentation')) return 'document'
  if (mimeType.includes('zip') || mimeType.includes('archive') || mimeType.includes('compressed')) return 'archive'
  return 'default'
}

export function canPreviewFile(mimeType: string): boolean {
  return (
    mimeType.startsWith('image/') ||
    mimeType.startsWith('text/') ||
    mimeType === 'application/pdf' ||
    mimeType === 'application/json' ||
    mimeType === 'application/xml'
  )
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    return 'Today'
  } else if (diffDays === 1) {
    return 'Yesterday'
  } else if (diffDays < 7) {
    return `${diffDays} days ago`
  } else {
    return date.toLocaleDateString()
  }
}